import type { FilterClause } from 'tio-common/utils/open-search-builder';

const COURSE_APPROVAL_STATES = ['TAS.ApplicationState.PENDING_COURSES_APPROVAL'] as const;
const EVIDENCE_REVIEW_STATES = ['TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL'] as const;
const PROGRAM_APPROVAL_STATES = ['TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL'];
const COMPLETION_APPROVAL_STATES = ['TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL'];
const WITHDRAWAL_APPROVAL_STATES = ['TAS.ProgramInstanceState.PENDING_WITHDRAWAL_APPROVAL'];

export type PendingStatusType =
  'all' | 'program' | 'courses' | 'evidence' | 'completion' | 'withdrawal' | 'tas_approvable';

/**
 * Keyword fields holding the approver role ("dot") of the current reviewer, per approval
 * step. Only the two application-level steps are here, deliberately:
 *
 * - The three program-instance steps all index their role from `program_approval`
 *   regardless of step (tio-api `Indices::Tas::CommonBuilder#tas_program_instance_transition_metadata`
 *   passes `json_key: 'program_approval'` for approval/completion/withdrawal alike), so the
 *   completion and withdrawal values are not step-specific and go empty once program
 *   approval is done.
 * - Card-usage rows (TasAsset) carry no role on this index at all; the approvable role
 *   lives on TasApprovable's own index as `current_reviewer_dots_keyword`.
 * - `tas_application_evidence_2_*` is omitted because it shares the `evidence_approval`
 *   source with `tas_application_evidence_*` — the two values are always identical.
 */
export const APPROVAL_ROLE_FIELDS = {
  courses: 'tas_application_courses_current_approval_role_keyword',
  evidence: 'tas_application_evidence_current_approval_role_keyword',
} as const;

export type RoleFilterableStatus = keyof typeof APPROVAL_ROLE_FIELDS;

export type ApprovalRoleFacet = { name: string; field: string };

export type ApprovalRoleBucket = { key: string; count: number };

/**
 * Whether an application row should open in the admin read-only view.
 *
 * @param rowId stable queue-row identifier, regardless of document grain
 * @param reviewerEmployeeId current user's employee id
 * @param currentReviewerIds employee ids assigned to the application's current approval step
 * @returns true only when reviewer metadata is present and the current user is not assigned to it
 */
export function isApprovalQueueApplicationReadOnly(
  rowId: string | undefined,
  reviewerEmployeeId: string | undefined,
  currentReviewerIds: readonly (string | number)[] | undefined
): boolean {
  if (!rowId || !reviewerEmployeeId || !currentReviewerIds) return false;

  return !currentReviewerIds.some((id) => String(id) === reviewerEmployeeId);
}

/** Approver roles number well under twenty, so one bucket page always covers them. */
export const APPROVAL_ROLE_FACET_SIZE = 50;

function roleFilterableStatuses(status: PendingStatusType): RoleFilterableStatus[] {
  const keys = Object.keys(APPROVAL_ROLE_FIELDS) as RoleFilterableStatus[];
  return status === 'all' ? keys : keys.filter((key) => key === status);
}

/**
 * Aggregation name/field pairs backing the reviewer-role facet for a status selection.
 * Empty for the steps that carry no usable role data, which is how callers know to hide
 * the filter. Names are camelCase because the API deep-camelizes response keys.
 */
export function approvalRoleFacetsFor(status: PendingStatusType = 'all'): ApprovalRoleFacet[] {
  return roleFilterableStatuses(status).map((key) => ({
    name: `facetRole${key.charAt(0).toUpperCase()}${key.slice(1)}`,
    field: APPROVAL_ROLE_FIELDS[key],
  }));
}

/**
 * Match rows whose current reviewer holds `role` (a full dot, e.g.
 * `TAS.Approver.SUPERVISOR`). Returns undefined when the selection cannot apply so callers
 * can skip the clause entirely.
 *
 * This belongs in `post_filter`, not filter context: aggregations are computed before
 * post_filter, so the facet keeps offering its sibling roles instead of collapsing to the
 * one already chosen.
 */
export function buildApprovalRoleClause(
  role: string | null | undefined,
  fields: string[]
): FilterClause | undefined {
  if (!role || fields.length === 0) {
    return undefined;
  }

  return {
    bool: {
      should: fields.map((field) => ({ term: { [field]: role } })),
      minimum_should_match: 1,
    },
  } as FilterClause;
}

/**
 * Fold the per-step role aggregations into one option list, summing counts for a role that
 * appears in more than one step. Exact rather than approximate: an application sits in a
 * single state, and each step's field is cleared once that step is affirmed, so no document
 * contributes to two buckets.
 */
export function mergeApprovalRoleBuckets(
  bucketGroups: { key: string; count: number }[][]
): ApprovalRoleBucket[] {
  const totals = new Map<string, number>();

  for (const buckets of bucketGroups) {
    for (const { key, count } of buckets) {
      if (!key) {
        continue;
      }
      totals.set(key, (totals.get(key) ?? 0) + count);
    }
  }

  return [...totals.entries()]
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
}

/**
 * Leaf of an approver dot, lowercased — the key used under `user-roles.tas.approver.*`.
 * `TAS.Approver.DEPARTMENT_HEAD` → `department_head`.
 */
export function approverRoleTranslationKey(dot: string): string {
  return (dot.split('.').pop() || dot).toLowerCase();
}

/**
 * Segments of a `TAS.Approver.*` dot that are acronyms rather than words — title-casing them
 * blindly reads "Tio", "Tap", "Ppp". These are the acronyms present in the enum today
 * (`db/seeds/fixtures/tas/approver.json`); a new role with a new acronym needs adding here.
 */
const APPROVER_ROLE_ACRONYMS = new Set(['PPP', 'TAP', 'TIO']);

/**
 * Readable fallback for a dot with no `user-roles.tas.approver.*` entry. All thirteen enum
 * values have one, so this covers only a role added to the enum ahead of its translation.
 */
export function humanizeApproverRole(dot: string): string {
  return (dot.split('.').pop() || dot)
    .split('_')
    .filter(Boolean)
    .map((word) =>
      APPROVER_ROLE_ACRONYMS.has(word.toUpperCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join(' ');
}

/** `record_type` values sharing the TasProgramInstance index. */
type RecordGrain = 'application' | 'instance' | 'asset';

function grainScopedClause(grain: RecordGrain, clause: object) {
  return {
    bool: {
      filter: [{ term: { record_type: grain } }, clause],
    },
  };
}

/**
 * OpenSearch `should` clauses for the approval queue, one per record grain.
 *
 * Each clause is a `bool.filter` pairing a `record_type` term with its state predicate rather
 * than a bare leaf clause: the three grains share one index, and asset rows carry a copy of
 * their parents' state that goes stale, so an unscoped state term over-matches.
 *
 * @param status pending bucket to match; `all` unions every grain
 * @param options.includeTasApprovable drops the card-usage-review (asset) clause when false
 * @returns clauses for `bool.should` — OR-combined by the caller
 */
export function buildApprovalShouldClauses(
  status: PendingStatusType = 'all',
  { includeTasApprovable = true }: { includeTasApprovable?: boolean } = {}
): FilterClause[] {
  const shouldClauses: unknown[] = [];
  const appStates: string[] = [];
  const instanceStates: string[] = [];

  // Collect application states
  if (status === 'all' || status === 'courses') {
    appStates.push(...COURSE_APPROVAL_STATES);
  }

  if (status === 'all' || status === 'evidence') {
    appStates.push(...EVIDENCE_REVIEW_STATES);
  }

  // Collect instance states
  if (status === 'all' || status === 'program') {
    instanceStates.push(...PROGRAM_APPROVAL_STATES);
  }

  if (status === 'all' || status === 'completion') {
    instanceStates.push(...COMPLETION_APPROVAL_STATES);
  }

  if (status === 'all' || status === 'withdrawal') {
    instanceStates.push(...WITHDRAWAL_APPROVAL_STATES);
  }

  // Build optimized clauses - ONE per field instead of multiple
  if (appStates.length > 0) {
    shouldClauses.push(
      grainScopedClause('application', {
        terms: {
          tas_application_state_keyword: appStates,
        },
      })
    );
  }

  if (instanceStates.length > 0) {
    shouldClauses.push(
      grainScopedClause('instance', {
        terms: {
          tas_program_instance_state_keyword: instanceStates,
        },
      })
    );
  }

  // TasAsset documents with pending approvables (card usage review)
  if (includeTasApprovable && (status === 'all' || status === 'tas_approvable')) {
    shouldClauses.push(
      grainScopedClause('asset', {
        term: {
          tas_asset_has_pending_approvables_boolean: true,
        },
      })
    );
  }

  return shouldClauses as FilterClause[];
}
